<template>
  <div>
    <report-panel-collapsible :report="report" :anchor="'balancesheet'" :initally-open="showDiff">
      <div slot="title">Balance Sheet</div>
      <div slot="body">
        <!-- panel content start -->
        <div class="table-responsive">
          <table class="table table-striped table-condensed" id="balance-sheet-table">
            <thead>
              <tr>
                <header-cell>
                  <template slot="heading">
                    <h5>Fixed Assets</h5>
                  </template>
                </header-cell>
                <th
                  class="text-right"
                  v-for="(financial, index) of financialReports"
                  :key="financial.id"
                  :class="extraClasses(index, 'accounts_date')"
                >
                  {{ isEmptyMessage(formatDate(financial.accounts_date)) }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <header-cell>
                  <template slot="heading">Assets Intangible:</template>
                </header-cell>
                <standard-cell
                  v-for="(financial, index) of financialReports"
                  :key="financial.id"
                  :currencySymbol="financial.currency.html_symbol"
                  :currencyValue="financial.assets_intangible"
                  :percentageValue="financial.assets_intangible_delta_percentage"
                  :direction="financial.assets_intangible_delta_direction"
                  :isCreditSafeReport="report.is_creditsafe_report"
                  :showPercentage="true"
                  :extra-classes="extraClasses(index, 'assets_intangible')"
                />
              </tr>
              <tr>
                <header-cell>
                  <template slot="heading">Assets Tangible:</template>
                </header-cell>
                <standard-cell
                  v-for="(financial, index) of financialReports"
                  :key="financial.id"
                  :currencySymbol="financial.currency.html_symbol"
                  :currencyValue="financial.assets_tangible"
                  :percentageValue="financial.assets_tangible_delta_percentage"
                  :direction="financial.assets_tangible_delta_direction"
                  :isCreditSafeReport="report.is_creditsafe_report"
                  :showPercentage="true"
                  :extra-classes="extraClasses(index, 'assets_tangible')"
                />
              </tr>
              <tr>
                <header-cell>
                  <template slot="heading">Investments:</template>
                </header-cell>
                <standard-cell
                  v-for="(financial, index) of financialReports"
                  :key="financial.id"
                  :currencySymbol="financial.currency.html_symbol"
                  :currencyValue="financial.investments"
                  :percentageValue="financial.investments_delta_percentage"
                  :direction="financial.investments_delta_direction"
                  :isCreditSafeReport="report.is_creditsafe_report"
                  :showPercentage="true"
                  :extra-classes="extraClasses(index, 'investments')"
                />
              </tr>
              <tr class="totalRow">
                <header-cell>
                  <template slot="heading">Total Fixed Assets:</template>
                </header-cell>
                <standard-cell
                  v-for="(financial, index) of financialReports"
                  :key="financial.id"
                  :currencySymbol="financial.currency.html_symbol"
                  :currencyValue="financial.total_fixed_assets"
                  :percentageValue="financial.total_fixed_assets_delta_percentage"
                  :direction="financial.total_fixed_assets_delta_direction"
                  :isCreditSafeReport="report.is_creditsafe_report"
                  :showPercentage="true"
                  :extra-classes="extraClasses(index, 'total_fixed_assets')"
                />
              </tr>
              <tr class="spaceRow">
                <td colspan="6">&nbsp;</td>
              </tr>
              <tr>
                <header-cell>
                  <template slot="heading">
                    <h5>Current Assets</h5>
                  </template>
                </header-cell>
                <th
                  class="text-right"
                  v-for="(financial, index) of financialReports"
                  :key="financial.id"
                  :class="extraClasses(index, 'accounts_date')"
                >
                  {{ isEmptyMessage(formatDate(financial.accounts_date)) }}
                </th>
              </tr>
              <tr>
                <header-cell>
                  <template slot="heading">Trade Debtors:</template>
                </header-cell>
                <standard-cell
                  v-for="(financial, index) of financialReports"
                  :key="financial.id"
                  :currencySymbol="financial.currency.html_symbol"
                  :currencyValue="financial.trade_debtors"
                  :percentageValue="financial.trade_debtors_delta_percentage"
                  :direction="financial.trade_debtors_delta_direction"
                  :isCreditSafeReport="report.is_creditsafe_report"
                  :showPercentage="true"
                  :extra-classes="extraClasses(index, 'trade_debtors')"
                />
              </tr>
              <tr>
                <header-cell>
                  <template slot="heading">Stock:</template>
                </header-cell>
                <standard-cell
                  v-for="(financial, index) of financialReports"
                  :key="financial.id"
                  :currencySymbol="financial.currency.html_symbol"
                  :currencyValue="financial.stock"
                  :percentageValue="financial.stock_delta_percentage"
                  :direction="financial.stock_delta_direction"
                  :isCreditSafeReport="report.is_creditsafe_report"
                  :showPercentage="true"
                  :extra-classes="extraClasses(index, 'stock')"
                />
              </tr>
              <tr>
                <header-cell>
                  <template slot="heading">Cash:</template>
                </header-cell>
                <standard-cell
                  v-for="(financial, index) of financialReports"
                  :key="financial.id"
                  :currencySymbol="financial.currency.html_symbol"
                  :currencyValue="financial.cash"
                  :percentageValue="financial.cash_delta_percentage"
                  :direction="financial.cash_delta_direction"
                  :isCreditSafeReport="report.is_creditsafe_report"
                  :showPercentage="true"
                  :extra-classes="extraClasses(index, 'cash')"
                />
              </tr>
              <tr>
                <header-cell>
                  <template slot="heading">Other Debtors:</template>
                </header-cell>
                <standard-cell
                  v-for="(financial, index) of financialReports"
                  :key="financial.id"
                  :currencySymbol="financial.currency.html_symbol"
                  :currencyValue="financial.other_debtors"
                  :percentageValue="financial.other_debtors_delta_percentage"
                  :direction="financial.other_debtors_delta_direction"
                  :isCreditSafeReport="report.is_creditsafe_report"
                  :showPercentage="true"
                  :extra-classes="extraClasses(index, 'other_debtors')"
                />
              </tr>
              <tr class="totalRow">
                <header-cell>
                  <template slot="heading">Total Current Assets:</template>
                </header-cell>
                <standard-cell
                  v-for="(financial, index) of financialReports"
                  :key="financial.id"
                  :currencySymbol="financial.currency.html_symbol"
                  :currencyValue="financial.total_current_assets"
                  :percentageValue="financial.total_current_assets_delta_percentage"
                  :direction="financial.total_current_assets_delta_direction"
                  :isCreditSafeReport="report.is_creditsafe_report"
                  :showPercentage="true"
                  :extra-classes="extraClasses(index, 'total_current_assets')"
                />
              </tr>
              <tr class="spaceRow">
                <td colspan="6">&nbsp;</td>
              </tr>
              <tr>
                <header-cell>
                  <template slot="heading">
                    <h5>Current Liabilities</h5>
                  </template>
                </header-cell>
                <th
                  class="text-right"
                  v-for="(financial, index) of financialReports"
                  :key="financial.id"
                  :class="extraClasses(index, 'accounts_date')"
                >
                  {{ isEmptyMessage(formatDate(financial.accounts_date)) }}
                </th>
              </tr>
              <tr>
                <header-cell>
                  <template slot="heading">Trade Creditors:</template>
                </header-cell>
                <standard-cell
                  v-for="(financial, index) of financialReports"
                  :key="financial.id"
                  :currencySymbol="financial.currency.html_symbol"
                  :currencyValue="financial.trade_creditors"
                  :percentageValue="financial.trade_creditors_delta_percentage"
                  :direction="financial.trade_creditors_delta_direction"
                  :isCreditSafeReport="report.is_creditsafe_report"
                  :showPercentage="true"
                  :extra-classes="extraClasses(index, 'trade_creditors')"
                />
              </tr>
              <tr>
                <header-cell>
                  <template slot="heading">Loans and Short Term Debt:</template>
                </header-cell>
                <standard-cell
                  v-for="(financial, index) of financialReports"
                  :key="financial.id"
                  :currencySymbol="financial.currency.html_symbol"
                  :currencyValue="financial.loans_and_short_term_debt"
                  :percentageValue="financial.loans_and_short_term_debt_delta_percentage"
                  :direction="financial.loans_and_short_term_debt_delta_direction"
                  :isCreditSafeReport="report.is_creditsafe_report"
                  :showPercentage="true"
                  :extra-classes="extraClasses(index, 'loans_and_short_term_debt')"
                />
              </tr>
              <tr>
                <header-cell>
                  <template slot="heading">Other Creditors:</template>
                </header-cell>
                <standard-cell
                  v-for="(financial, index) of financialReports"
                  :key="financial.id"
                  :currencySymbol="financial.currency.html_symbol"
                  :currencyValue="financial.other_creditors"
                  :percentageValue="financial.other_creditors_delta_percentage"
                  :direction="financial.other_creditors_delta_direction"
                  :isCreditSafeReport="report.is_creditsafe_report"
                  :showPercentage="true"
                  :extra-classes="extraClasses(index, 'other_creditors')"
                />
              </tr>
              <tr class="totalRow">
                <header-cell>
                  <template slot="heading">Total Current Liabilities:</template>
                </header-cell>
                <standard-cell
                  v-for="(financial, index) of financialReports"
                  :key="financial.id"
                  :currencySymbol="financial.currency.html_symbol"
                  :currencyValue="financial.total_current_liabilities"
                  :percentageValue="financial.total_current_liabilities_delta_percentage"
                  :direction="financial.total_current_liabilities_delta_direction"
                  :isCreditSafeReport="report.is_creditsafe_report"
                  :showPercentage="true"
                  :extra-classes="extraClasses(index, 'total_current_liabilities')"
                />
              </tr>
              <tr class="spaceRow">
                <td colspan="6">&nbsp;</td>
              </tr>
              <tr class="totalRow">
                <header-cell>
                  <template slot="heading">Net Current Assets:</template>
                </header-cell>
                <standard-cell
                  v-for="(financial, index) of financialReports"
                  :key="financial.id"
                  :currencySymbol="financial.currency.html_symbol"
                  :currencyValue="financial.net_current_assets"
                  :percentageValue="financial.net_current_assets_delta_percentage"
                  :direction="financial.net_current_assets_delta_direction"
                  :isCreditSafeReport="report.is_creditsafe_report"
                  :showPercentage="true"
                  :extra-classes="extraClasses(index, 'net_current_assets')"
                />
              </tr>
              <tr class="spaceRow">
                <td colspan="6">&nbsp;</td>
              </tr>
              <tr class="totalRow">
                <header-cell>
                  <template slot="heading">Total Assets - Current Liabilities:</template>
                </header-cell>
                <standard-cell
                  v-for="(financial, index) of financialReports"
                  :key="financial.id"
                  :currencySymbol="financial.currency.html_symbol"
                  :currencyValue="financial.total_assets_less_liabilities"
                  :percentageValue="financial.total_assets_less_liabilities_delta_percentage"
                  :direction="financial.total_assets_less_liabilities_delta_direction"
                  :isCreditSafeReport="report.is_creditsafe_report"
                  :showPercentage="true"
                  :extra-classes="extraClasses(index, 'total_assets_less_liabilities')"
                />
              </tr>
              <tr class="spaceRow">
                <td colspan="6">&nbsp;</td>
              </tr>
              <tr>
                <header-cell>
                  <template slot="heading">Total Longterm Liabilities:</template>
                </header-cell>
                <standard-cell
                  v-for="(financial, index) of financialReports"
                  :key="financial.id"
                  :currencySymbol="financial.currency.html_symbol"
                  :currencyValue="financial.total_longterm_liabilities"
                  :percentageValue="financial.total_longterm_liabilities_delta_percentage"
                  :direction="financial.total_longterm_liabilities_delta_direction"
                  :isCreditSafeReport="report.is_creditsafe_report"
                  :showPercentage="true"
                  :extra-classes="extraClasses(index, 'total_longterm_liabilities')"
                />
              </tr>
              <tr class="spaceRow">
                <td colspan="6">&nbsp;</td>
              </tr>
              <tr class="totalRow">
                <header-cell>
                  <template slot="heading">Net Assets (Liabilities):</template>
                </header-cell>
                <standard-cell
                  v-for="(financial, index) of financialReports"
                  :key="financial.id"
                  :currencySymbol="financial.currency.html_symbol"
                  :currencyValue="financial.net_assets_or_liabilities"
                  :percentageValue="financial.net_assets_or_liabilities_delta_percentage"
                  :direction="financial.net_assets_or_liabilities_delta_direction"
                  :isCreditSafeReport="report.is_creditsafe_report"
                  :showPercentage="true"
                  :extra-classes="extraClasses(index, 'net_assets_or_liabilities')"
                />
              </tr>
              <tr class="spaceRow">
                <td colspan="6">&nbsp;</td>
              </tr>
              <tr>
                <header-cell>
                  <template slot="heading">
                    <h5>Capital and Reserves</h5>
                  </template>
                </header-cell>
                <th
                  class="text-right"
                  v-for="(financial, index) of financialReports"
                  :key="financial.id"
                  :class="extraClasses(index, 'accounts_date')"
                >
                  {{ isEmptyMessage(formatDate(financial.accounts_date)) }}
                </th>
              </tr>
              <tr>
                <header-cell>
                  <template slot="heading">Share Capital:</template>
                </header-cell>
                <standard-cell
                  v-for="(financial, index) of financialReports"
                  :key="financial.id"
                  :currencySymbol="financial.currency.html_symbol"
                  :currencyValue="financial.share_capital"
                  :percentageValue="financial.share_capital_delta_percentage"
                  :direction="financial.share_capital_delta_direction"
                  :isCreditSafeReport="report.is_creditsafe_report"
                  :showPercentage="true"
                  :extra-classes="extraClasses(index, 'share_capital')"
                />
              </tr>
              <tr>
                <header-cell>
                  <template slot="heading">Revaluation Reserves:</template>
                </header-cell>
                <standard-cell
                  v-for="(financial, index) of financialReports"
                  :key="financial.id"
                  :currencySymbol="financial.currency.html_symbol"
                  :currencyValue="financial.revaluation_reserves"
                  :percentageValue="financial.revaluation_reserves_delta_percentage"
                  :direction="financial.revaluation_reserves_delta_direction"
                  :isCreditSafeReport="report.is_creditsafe_report"
                  :showPercentage="true"
                  :extra-classes="extraClasses(index, 'revaluation_reserves')"
                />
              </tr>
              <tr>
                <header-cell>
                  <template slot="heading">Sundry Reserves:</template>
                </header-cell>
                <standard-cell
                  v-for="(financial, index) of financialReports"
                  :key="financial.id"
                  :currencySymbol="financial.currency.html_symbol"
                  :currencyValue="financial.sundry_reserves"
                  :percentageValue="financial.sundry_reserves_delta_percentage"
                  :direction="financial.sundry_reserves_delta_direction"
                  :isCreditSafeReport="report.is_creditsafe_report"
                  :showPercentage="true"
                  :extra-classes="extraClasses(index, 'sundry_reserves')"
                />
              </tr>
              <tr>
                <header-cell>
                  <template slot="heading">Profit and Loss Account Reserve:</template>
                </header-cell>
                <standard-cell
                  v-for="(financial, index) of financialReports"
                  :key="financial.id"
                  :currencySymbol="financial.currency.html_symbol"
                  :currencyValue="financial.profit_and_loss_account_reserve"
                  :percentageValue="financial.profit_and_loss_account_reserve_delta_percentage"
                  :direction="financial.profit_and_loss_account_reserve_delta_direction"
                  :isCreditSafeReport="report.is_creditsafe_report"
                  :showPercentage="true"
                  :extra-classes="extraClasses(index, 'profit_and_loss_account_reserve')"
                />
              </tr>
              <tr class="totalRow">
                <header-cell>
                  <template slot="heading">Shareholder Funds:</template>
                </header-cell>
                <standard-cell
                  v-for="(financial, index) of financialReports"
                  :key="financial.id"
                  :currencySymbol="financial.currency.html_symbol"
                  :currencyValue="financial.shareholder_funds"
                  :percentageValue="financial.shareholder_funds_delta_percentage"
                  :direction="financial.shareholder_funds_delta_direction"
                  :isCreditSafeReport="report.is_creditsafe_report"
                  :showPercentage="true"
                  :extra-classes="extraClasses(index, 'shareholder_funds')"
                />
              </tr>
            </tbody>
          </table>
        </div>
        <!-- panel content end -->
      </div>
    </report-panel-collapsible>
  </div>
</template>

<script>
import toolsMixin from '@/components/reports/ToolsMixin';
import ReportPanelCollapsible from '@/components/reports/components/ReportPanelCollapsible';
import HeaderCell from '@/components/reports/panels/financials/tablecells/HeaderCell';
import StandardCell from '@/components/reports/panels/financials/tablecells/StandardCell';
import financialsMixin from './FinancialsMixin';

export default {
  components: {
    ReportPanelCollapsible,
    HeaderCell,
    StandardCell,
  },
  mixins: [financialsMixin, toolsMixin],
  props: ['report', 'showDiff'],
  data() {
    return {
      maxNumberOfFinancialsToShow: 5,
    };
  },
};
</script>
